import { useEffect, useState } from 'react';

import { Card } from './ui';
import { api } from '../lib/api';
import { ago } from '../lib/format';

interface Credential {
  ref: string;
  provider: 'ANTHROPIC' | 'BEDROCK' | 'VERTEX';
  label: string;
  hint: string | null;
  meta: Record<string, unknown>;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastError: string | null;
  updatedAt: string;
}

type Provider = Credential['provider'];

/**
 * The fields each provider needs, declared rather than hard-coded into JSX.
 *
 * `secret: true` fields are sent once and never come back — the API has no
 * endpoint that can return them. `secret: false` ones are ordinary settings
 * stored beside the credential and are readable, because an AWS region is not a
 * secret and pretending otherwise just makes the page harder to check.
 */
const FORMS: Record<Provider, {
  title: string;
  blurb: string;
  fields: Array<{ name: string; label: string; secret: boolean; hint?: string; textarea?: boolean }>;
  metaFields?: Array<{ name: string; label: string; hint?: string }>;
}> = {
  ANTHROPIC: {
    title: 'Claude Platform (Anthropic)',
    blurb:
      'An Admin key from Console → Settings → Admin keys, created by an organisation owner. ' +
      'It must start with sk-ant-admin — an ordinary API key cannot read usage or cost.',
    fields: [{ name: 'adminKey', label: 'Admin API key', secret: true, hint: 'sk-ant-admin01…' }],
  },
  BEDROCK: {
    title: 'AWS Bedrock',
    blurb:
      'The access key for the read-only IAM user (CloudWatch + Cost Explorer). This is not the ' +
      'AWS_BEARER_TOKEN_BEDROCK your apps use to invoke models — that one cannot read billing.',
    fields: [
      { name: 'accessKeyId', label: 'Access key ID', secret: false, hint: 'AKIA…' },
      { name: 'secretAccessKey', label: 'Secret access key', secret: true },
    ],
    metaFields: [
      { name: 'regions', label: 'Regions', hint: 'Comma separated, e.g. eu-central-1, ap-southeast-2' },
    ],
  },
  VERTEX: {
    title: 'Google Vertex AI',
    blurb: 'A service account JSON key with Monitoring Viewer, plus BigQuery read on the billing export.',
    fields: [{ name: 'serviceAccountJson', label: 'Service account JSON', secret: true, textarea: true }],
    metaFields: [
      { name: 'projectId', label: 'Project ID' },
      { name: 'billingDataset', label: 'Billing export dataset', hint: 'project.dataset.table' },
    ],
  },
};

const PROVIDERS = Object.keys(FORMS) as Provider[];

function AddForm({ onSaved }: { onSaved: () => void }) {
  const [provider, setProvider] = useState<Provider>('ANTHROPIC');
  const [ref, setRef] = useState('');
  const [label, setLabel] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [meta, setMeta] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const form = FORMS[provider];

  const change = (p: Provider) => {
    // Values are cleared on switch rather than kept: they belong to the old
    // provider's fields, and a stale secret silently carried across is the kind
    // of thing that gets stored under the wrong name.
    setProvider(p);
    setValues({});
    setMeta({});
    setError(null);
    setOk(null);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api('/credentials', {
        method: 'PUT',
        body: JSON.stringify({
          ref: ref.trim(),
          provider,
          label: label.trim(),
          secret: values,
          meta: Object.fromEntries(Object.entries(meta).filter(([, v]) => v.trim() !== '')),
        }),
      });
      // Cleared immediately on success: there is no reason for a pasted admin key
      // to stay in a DOM node after it has been stored.
      setValues({});
      setMeta({});
      setRef('');
      setLabel('');
      setOk('Stored. Use Verify to confirm it works.');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not store that credential');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Add or replace a credential">
      <div className="cred-tabs">
        {PROVIDERS.map((p) => (
          <button key={p} type="button" className={p === provider ? 'tab tab-on' : 'tab'}
            onClick={() => change(p)}>
            {FORMS[p].title}
          </button>
        ))}
      </div>

      <p className="muted small">{form.blurb}</p>

      <div className="form-grid">
        <label>
          Reference
          <input value={ref} onChange={(e) => setRef(e.target.value)}
            placeholder={provider === 'BEDROCK' ? 'aws-bedrock' : 'anthropic-admin'} />
          <span className="muted small">A short name this credential is referred to by.</span>
        </label>
        <label>
          Label
          <input value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="What this is for" />
        </label>

        {form.fields.map((f) => (
          <label key={f.name} className={f.textarea ? 'wide' : undefined}>
            {f.label}
            {f.textarea ? (
              <textarea rows={6} value={values[f.name] ?? ''} spellCheck={false}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} />
            ) : (
              <input
                // Secret fields are password inputs so a shoulder, a screen share
                // or a screenshot does not capture the value.
                type={f.secret ? 'password' : 'text'}
                autoComplete="off"
                spellCheck={false}
                value={values[f.name] ?? ''}
                placeholder={f.hint}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
              />
            )}
            {f.secret && <span className="muted small">Write-only — it can never be read back.</span>}
          </label>
        ))}

        {form.metaFields?.map((m) => (
          <label key={m.name}>
            {m.label}
            <input value={meta[m.name] ?? ''} placeholder={m.hint}
              onChange={(e) => setMeta({ ...meta, [m.name]: e.target.value })} />
          </label>
        ))}
      </div>

      <div className="cost-actions">
        <button type="button" disabled={busy || !ref.trim()} onClick={() => void submit()}>
          {busy ? 'Storing…' : 'Store credential'}
        </button>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}
      {ok && <p className="notice">{ok}</p>}
    </Card>
  );
}

function Row({ c, onChanged }: { c: Credential; onChanged: () => void }) {
  const [busy, setBusy] = useState<'test' | 'delete' | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const test = async () => {
    setBusy('test');
    setResult(null);
    try {
      const r = await api<{ ok: boolean; detail: string }>(`/credentials/${c.ref}/test`, { method: 'POST' });
      setResult(r.detail);
    } catch (e) {
      setResult(e instanceof Error ? e.message : 'Verification failed');
    } finally {
      setBusy(null);
      onChanged();
    }
  };

  const remove = async () => {
    setBusy('delete');
    try {
      await api(`/credentials/${c.ref}`, { method: 'DELETE' });
      onChanged();
    } finally {
      setBusy(null);
      setConfirming(false);
    }
  };

  const regions = typeof c.meta.regions === 'string' ? c.meta.regions : null;

  return (
    <tr>
      <td><code className="stack-name">{c.ref}</code></td>
      <td>{FORMS[c.provider]?.title ?? c.provider}</td>
      <td className="mono small">{c.hint ?? <span className="muted">—</span>}</td>
      <td className="small">
        {c.label}
        {regions && <><br /><span className="muted">{regions}</span></>}
      </td>
      <td>
        {c.lastTestedAt === null ? (
          <span className="muted small">not verified</span>
        ) : c.lastTestOk ? (
          <span className="pill pill-up"><span aria-hidden="true">●</span> verified</span>
        ) : (
          <span className="pill pill-down" title={c.lastError ?? ''}><span aria-hidden="true">■</span> failed</span>
        )}
        {c.lastTestedAt && <div className="muted small">{ago(c.lastTestedAt)}</div>}
      </td>
      <td>
        {confirming ? (
          <span className="inline-confirm">
            <button type="button" className="small" onClick={() => void remove()}>Delete</button>
            <button type="button" className="ghost small" onClick={() => setConfirming(false)}>No</button>
          </span>
        ) : (
          <span className="inline-confirm">
            <button type="button" className="ghost small" disabled={busy !== null} onClick={() => void test()}>
              {busy === 'test' ? 'Checking…' : 'Verify'}
            </button>
            <button type="button" className="ghost small" onClick={() => setConfirming(true)}>Remove</button>
          </span>
        )}
        {result && <div className="muted small cred-result">{result}</div>}
        {!result && c.lastTestOk === false && c.lastError && (
          <div className="form-error small cred-result">{c.lastError}</div>
        )}
      </td>
    </tr>
  );
}

/** The credentials section of the admin panel. Superadmin only; the API enforces it. */
export function Credentials() {
  const [rows, setRows] = useState<Credential[] | null>(null);
  const [keyProblem, setKeyProblem] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);

  const load = () => {
    api<Credential[]>('/credentials').then(setRows).catch(() => setDenied(true));
    api<{ configured: boolean; problem: string | null }>('/credentials/key-status')
      .then((s) => setKeyProblem(s.configured ? null : s.problem))
      .catch(() => undefined);
  };
  useEffect(load, []);

  if (denied) return null;

  return (
    <>
      <Card title="Provider credentials">
        <p className="muted small">
          Keys for the LLM platforms PlatformIQ reads usage and spend from. Stored encrypted in the
          database and <strong>never readable again</strong> — not by this page, not by the API, not
          by anyone signed in. Replace one to change it.
        </p>

        {keyProblem && (
          <div className="notice notice-bad">
            <p><strong>Credentials cannot be stored yet.</strong> {keyProblem}</p>
            <p className="small">
              Add <code>CREDENTIALS_KEY</code> to <code>/opt/platformiq/.env</code> and recreate the
              API container.
            </p>
          </div>
        )}

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Reference</th>
                <th scope="col">Provider</th>
                <th scope="col">Key</th>
                <th scope="col">Label</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).map((c) => <Row key={c.ref} c={c} onChanged={load} />)}
            </tbody>
          </table>
        </div>
        {rows !== null && rows.length === 0 && (
          <p className="empty">Nothing stored yet.</p>
        )}
      </Card>

      <AddForm onSaved={load} />
    </>
  );
}
